import React from 'react';
import './MessageBubble.css';

const MessageBubble = ({ message }) => {
  const {
    sender_type,
    message_type,
    content,
    timestamp,
    metadata,
    file_url
  } = message;

  // Determine alignment based on sender
  const isRightAligned = sender_type === 'tenant';
  const bubbleClass = `message-bubble ${isRightAligned ? 'right' : 'left'} ${message_type}`;

  // Format timestamp
  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get sender icon/emoji
  const getSenderIcon = (senderType) => {
    const icons = {
      'tenant': '👤',
      'legal_team': '⚖️',
      'system': '🤖'
    };
    return icons[senderType] || '•';
  };

  // Get message type badge
  const getMessageTypeBadge = (msgType) => {
    if (msgType === 'text') return null;

    const badges = {
      'file': { icon: '📎', label: 'File', color: '#0084ff' },
      'action': { icon: '⚡', label: 'Action', color: '#ff9500' },
      'status_update': { icon: '✓', label: 'Status', color: '#25d366' }
    };

    const badge = badges[msgType];
    if (!badge) return null;

    return (
      <span className="message-type-badge" style={{ backgroundColor: badge.color }}>
        <span className="badge-icon">{badge.icon}</span>
        {badge.label}
      </span>
    );
  };

  // Render file preview if file message
  const renderFilePreview = () => {
    if (message_type !== 'file' || !metadata?.file_name) return null;

    const fileName = metadata.file_name;
    const fileExt = fileName.split('.').pop().toLowerCase();
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExt);
    const isPDF = fileExt === 'pdf';

    return (
      <div className="file-preview">
        <div className="file-icon">
          {isImage && '🖼️'}
          {isPDF && '📄'}
          {!isImage && !isPDF && '📎'}
        </div>
        <div className="file-info">
          <span className="file-name">{fileName}</span>
          {metadata.file_size && (
            <span className="file-size">
              {(metadata.file_size / 1024).toFixed(1)} KB
            </span>
          )}
        </div>
      </div>
    );
  };

  // Render metadata if present
  const renderMetadata = () => {
    if (!metadata || Object.keys(metadata).length === 0) return null;

    // Don't show file metadata as it's already in preview
    if (message_type === 'file') return null;

    return (
      <div className="message-metadata">
        {metadata.page && <span>Page {metadata.page}</span>}
        {metadata.extraction_status && (
          <span className={`extraction-status ${metadata.extraction_status}`}>
            {metadata.extraction_status === 'success' ? '✓ Success' : '✗ Failed'}
          </span>
        )}
        {metadata.action_type && <span>Action: {metadata.action_type}</span>}
      </div>
    );
  };

  return (
    <div className={`message-wrapper ${isRightAligned ? 'right' : 'left'}`}>
      {!isRightAligned && (
        <div className="sender-icon">{getSenderIcon(sender_type)}</div>
      )}

      <div className={bubbleClass}>
        <div className="message-header">
          {!isRightAligned && (
            <span className="sender-name">
              {sender_type === 'legal_team' ? 'Legal Team' : 
               sender_type === 'system' ? 'System' : 'You'}
            </span>
          )}
          {getMessageTypeBadge(message_type)}
        </div>

        {renderFilePreview()}

        <div className="message-content">
          {content.split('\n').map((line, i) => (
            <React.Fragment key={i}>
              {line}
              {i < content.split('\n').length - 1 && <br />}
            </React.Fragment>
          ))}
        </div>

        {renderMetadata()}

        <div className="message-footer">
          <span className="message-time">{formatTime(timestamp)}</span>
        </div>
      </div>

      {isRightAligned && (
        <div className="sender-icon">{getSenderIcon(sender_type)}</div>
      )}
    </div>
  );
};

export default MessageBubble;
